import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { XMarkIcon as XSolidIcon } from '@heroicons/react/24/solid';
import type { WhatsAppSendRecipient } from '../../models/MasterAdmin/whatsappHub';
import type { WhatsAppRecipientPreview } from '../../models/MasterAdmin/whatsappCampaign';
import { browseWhatsAppSendRecipients } from '../../services/MasterAdmin/whatsappCampaignService';
import { recipientKey } from './whatsappPreview';
import { formatDisplayPhone } from './whatsappUi';

export type RecipientSourceTab = 'contacts' | 'users' | 'vendors';
export type PlatformFilter = 'all' | 'web' | 'ios' | 'android';
export type VendorKindFilter = 'all' | 'doctor' | 'ambulance';

const SOURCE_TABS: { id: RecipientSourceTab; label: string }[] = [
  { id: 'contacts', label: 'Saved contacts' },
  { id: 'users', label: 'Users' },
  { id: 'vendors', label: 'Vendors' },
];

const PLATFORM_OPTIONS: { id: PlatformFilter; label: string }[] = [
  { id: 'all', label: 'All platforms' },
  { id: 'web', label: 'Web' },
  { id: 'ios', label: 'iOS' },
  { id: 'android', label: 'Android' },
];

const VENDOR_KIND_OPTIONS: { id: VendorKindFilter; label: string }[] = [
  { id: 'all', label: 'All vendors' },
  { id: 'doctor', label: 'Doctors' },
  { id: 'ambulance', label: 'Ambulance' },
];

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800';

function toSendRecipient(r: WhatsAppRecipientPreview): WhatsAppSendRecipient | null {
  const cc = r.countryCode || '+91';
  const pn = r.phoneNumber || r.phone?.replace(/\D/g, '').slice(-10) || '';
  if (!pn) return null;
  const linkedUserId = (r as WhatsAppRecipientPreview & { linkedUserId?: string | null }).linkedUserId;
  const linkedVendorId = (r as WhatsAppRecipientPreview & { linkedVendorId?: string | null }).linkedVendorId;
  return {
    key: recipientKey(cc, pn),
    countryCode: cc,
    phoneNumber: pn,
    displayName: r.name,
    maskedPhone: r.maskedPhone,
    source: r.source,
    recipientType: r.recipientType,
    recipientId: linkedUserId || linkedVendorId || r.recipientId,
    platform: r.platform,
    vendorKind: r.vendorKind,
    email: r.email ?? null,
    city: r.city ?? null,
    tags: r.tags ?? [],
  };
}

function platformLabel(platform?: string | null) {
  if (!platform) return null;
  const p = platform.toLowerCase();
  if (p === 'ios') return 'iOS';
  if (p === 'android') return 'Android';
  if (p === 'web') return 'Web';
  return platform;
}

function kindLabel(r: WhatsAppRecipientPreview | WhatsAppSendRecipient) {
  const vendorKind = 'vendorKind' in r ? r.vendorKind : null;
  const recipientType = 'recipientType' in r ? r.recipientType : null;
  const source = 'source' in r ? r.source : null;
  if (vendorKind === 'doctor-clinic') return 'Doctor';
  if (vendorKind === 'ambulance') return 'Ambulance';
  if (recipientType === 'user') return 'User';
  if (source === 'contact') return 'Contact';
  if (recipientType === 'vendor') return 'Vendor';
  return recipientType || 'Recipient';
}

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: (recipients: WhatsAppSendRecipient[]) => void;
  initialRecipients?: WhatsAppSendRecipient[];
  singleSelect?: boolean;
  allowedSources?: RecipientSourceTab[];
  title?: string;
};

const WhatsAppRecipientSelectModal: React.FC<Props> = ({
  open,
  onClose,
  onConfirm,
  initialRecipients = [],
  singleSelect = false,
  allowedSources,
  title,
}) => {
  const tabs = useMemo(
    () =>
      allowedSources?.length
        ? SOURCE_TABS.filter((t) => allowedSources.includes(t.id))
        : SOURCE_TABS,
    [allowedSources]
  );

  const [draft, setDraft] = useState<WhatsAppSendRecipient[]>(initialRecipients);
  const [source, setSource] = useState<RecipientSourceTab>(tabs[0]?.id || 'contacts');
  const [platform, setPlatform] = useState<PlatformFilter>('all');
  const [vendorKind, setVendorKind] = useState<VendorKindFilter>('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [browseRows, setBrowseRows] = useState<WhatsAppRecipientPreview[]>([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(initialRecipients);
    setSearch('');
    setDebouncedSearch('');
    setPlatform('all');
    setVendorKind('all');
    if (tabs[0]) setSource(tabs[0].id);
  }, [open, initialRecipients, tabs]);

  useEffect(() => {
    if (!tabs.some((t) => t.id === source) && tabs[0]) setSource(tabs[0].id);
  }, [tabs, source]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  const loadBrowse = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const res = await browseWhatsAppSendRecipients({
        source,
        platform: source === 'contacts' ? 'all' : platform,
        vendorKind: source === 'vendors' ? vendorKind : 'all',
        q: debouncedSearch || undefined,
        limit: 50000,
      });
      setBrowseRows(res.data || []);
      setBrowseTotal(res.meta?.total ?? res.data?.length ?? 0);
    } catch {
      setBrowseRows([]);
      setBrowseTotal(0);
    } finally {
      setLoading(false);
    }
  }, [open, source, platform, vendorKind, debouncedSearch]);

  useEffect(() => {
    void loadBrowse();
  }, [loadBrowse]);

  const selectedKeys = useMemo(() => new Set(draft.map((r) => r.key)), [draft]);

  const selectableRows = useMemo(
    () => browseRows.map((r) => ({ row: r, send: toSendRecipient(r) })).filter((x) => x.send),
    [browseRows]
  );

  const allVisibleSelected =
    !singleSelect &&
    selectableRows.length > 0 &&
    selectableRows.every((x) => selectedKeys.has(x.send!.key));

  const toggleRecipient = (row: WhatsAppRecipientPreview) => {
    const next = toSendRecipient(row);
    if (!next) return;
    if (singleSelect) {
      setDraft(selectedKeys.has(next.key) ? [] : [next]);
      return;
    }
    if (selectedKeys.has(next.key)) {
      setDraft((prev) => prev.filter((r) => r.key !== next.key));
    } else {
      setDraft((prev) => [...prev, next]);
    }
  };

  const toggleSelectAllVisible = () => {
    if (singleSelect) return;
    if (allVisibleSelected) {
      const visibleKeys = new Set(selectableRows.map((x) => x.send!.key));
      setDraft((prev) => prev.filter((r) => !visibleKeys.has(r.key)));
      return;
    }
    const merged = new Map(draft.map((r) => [r.key, r]));
    for (const { send } of selectableRows) {
      if (send) merged.set(send.key, send);
    }
    setDraft(Array.from(merged.values()));
  };

  const removeRecipient = (key: string) => {
    setDraft((prev) => prev.filter((r) => r.key !== key));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-3 sm:p-6">
      <div
        className="flex h-[min(92vh,760px)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900"
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Select recipients'}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              {title || (singleSelect ? 'Select recipient' : 'Select recipients')}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {singleSelect
                ? 'Pick one person from the filtered list.'
                : 'Filter the list, multi-select who should receive this campaign, then confirm.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 md:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.9fr)]">
          {/* Left: browse / filter list */}
          <div className="flex min-h-0 flex-col border-b border-gray-200 p-4 dark:border-gray-700 md:border-b-0 md:border-r">
            <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1 dark:bg-gray-800">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setSource(tab.id)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    source === tab.id
                      ? 'bg-white text-[#128C7E] shadow-sm dark:bg-gray-900'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {(source === 'users' || source === 'vendors') && (
              <div className="mt-3">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Platform
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {PLATFORM_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setPlatform(opt.id)}
                      className={`rounded-full px-2.5 py-1 text-xs ${
                        platform === opt.id
                          ? 'bg-[#25D366]/15 font-medium text-[#128C7E]'
                          : 'border border-gray-200 text-gray-600 dark:border-gray-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {source === 'vendors' && (
              <div className="mt-3">
                <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">
                  Vendor type
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {VENDOR_KIND_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setVendorKind(opt.id)}
                      className={`rounded-full px-2.5 py-1 text-xs ${
                        vendorKind === opt.id
                          ? 'bg-[#25D366]/15 font-medium text-[#128C7E]'
                          : 'border border-gray-200 text-gray-600 dark:border-gray-600'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="relative mt-3">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={
                  source === 'contacts'
                    ? 'Search saved contacts...'
                    : source === 'users'
                      ? 'Search users by name, phone, city...'
                      : 'Search vendors by name, phone, city...'
                }
                className={`${inputClass} pl-9`}
              />
            </div>

            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              {!singleSelect ? (
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    disabled={!selectableRows.length}
                    onChange={toggleSelectAllVisible}
                    className="rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
                  />
                  Select all{browseTotal > selectableRows.length ? ` loaded` : ''} ({selectableRows.length}
                  {browseTotal > selectableRows.length ? ` of ${browseTotal}` : ''})
                </label>
              ) : (
                <span>Select one recipient</span>
              )}
              <span>
                {browseTotal} match{browseTotal === 1 ? '' : 'es'}
              </span>
            </div>

            <div className="mt-2 min-h-0 flex-1 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700">
              {loading ? (
                <p className="px-3 py-8 text-center text-sm text-gray-500">Loading recipients...</p>
              ) : selectableRows.length === 0 ? (
                <p className="px-3 py-8 text-center text-sm text-gray-500">
                  No recipients found. Try another filter or search.
                </p>
              ) : (
                selectableRows.map(({ row, send }) => {
                  const checked = selectedKeys.has(send!.key);
                  return (
                    <label
                      key={send!.key}
                      className={`flex cursor-pointer items-start gap-3 border-b px-3 py-2.5 last:border-0 dark:border-gray-700 ${
                        checked ? 'bg-[#25D366]/5' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
                      }`}
                    >
                      <input
                        type={singleSelect ? 'radio' : 'checkbox'}
                        name={singleSelect ? 'wa-recipient-single' : undefined}
                        checked={checked}
                        onChange={() => toggleRecipient(row)}
                        className="mt-0.5 rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-gray-900 dark:text-white">{row.name}</span>
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-800">
                            {kindLabel(row)}
                          </span>
                          {platformLabel(row.platform) && (
                            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                              {platformLabel(row.platform)}
                            </span>
                          )}
                          {row.city ? (
                            <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                              {row.city}
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {formatDisplayPhone(row.countryCode || '+91', row.phoneNumber || row.phone || '')}
                        </span>
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Right: selected list */}
          <div className="flex min-h-0 flex-col bg-gray-50 p-4 dark:bg-gray-950/40">
            <div className="mb-3 flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Selected ({draft.length})
              </p>
              {draft.length > 0 && (
                <button
                  type="button"
                  onClick={() => setDraft([])}
                  className="text-xs font-medium text-red-600 hover:underline"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
              {draft.length === 0 ? (
                <p className="px-3 py-10 text-center text-sm text-gray-500">
                  {singleSelect
                    ? 'No recipient selected yet.'
                    : 'Nothing selected yet. Use filters on the left, then check people to add them here.'}
                </p>
              ) : (
                draft.map((r) => (
                  <div
                    key={r.key}
                    className="flex items-start gap-2 border-b border-gray-100 px-3 py-2.5 last:border-0 dark:border-gray-800"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                        {r.displayName}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {formatDisplayPhone(r.countryCode, r.phoneNumber)}
                        {r.platform ? ` · ${platformLabel(r.platform)}` : ''}
                        {r.city ? ` · ${r.city}` : ''}
                      </p>
                      <span className="mt-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600 dark:bg-gray-800">
                        {kindLabel(r)}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRecipient(r.key)}
                      className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-red-600 dark:hover:bg-gray-800"
                      aria-label={`Remove ${r.displayName}`}
                    >
                      <XSolidIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-4 py-3 dark:border-gray-700 sm:px-5">
          <p className="text-xs text-gray-500">
            {singleSelect
              ? draft.length
                ? `Ready: ${draft[0].displayName}`
                : 'Choose one recipient to continue'
              : `${draft.length} recipient${draft.length === 1 ? '' : 's'} ready to receive this campaign`}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={draft.length === 0}
              onClick={() => onConfirm(draft)}
              className="rounded-lg bg-[#25D366] px-4 py-2 text-sm font-semibold text-white hover:bg-[#1ebe5d] disabled:cursor-not-allowed disabled:opacity-50"
            >
              Done{draft.length ? ` · ${draft.length}` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppRecipientSelectModal;
